/**
 * PASTEL PALLET — THEME JS
 * Cart AJAX, Search Overlay, Mobile Menu, Wishlist, Product Interactions
 */

'use strict';

/* ─── CART STATE ─── */
const PastelCart = {
  count: 0,
  items: [],

  async get() {
    const res = await fetch('/cart.js');
    const data = await res.json();
    this.count = data.item_count;
    this.items = data.items;
    return data;
  },

  async add(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add item to cart.');
    }
    return await res.json();
  },

  async change(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    return await res.json();
  },

  formatMoney(cents) {
    return '₹' + (cents / 100).toLocaleString('en-IN');
  }
};

/* ─── CART DRAWER ─── */
/* Markup lives in sections/cart-drawer.liquid. Every cart call asks Shopify to
   re-render that section in the same response, so prices, coupons and the
   shipping bar always come from Liquid and this only swaps the HTML. */
const CartDrawer = {
  el: null,
  busy: false,
  opener: null,
  breakdownOpen: false,
  sectionId: 'cart-drawer',

  init() {
    this.el = document.getElementById('cart-drawer');
    if (!this.el) return;
    // Listeners sit on the outer element because its contents are replaced
    this.el.addEventListener('click', e => this.onClick(e));
    this.el.addEventListener('submit', e => this.onSubmit(e));
    this.el.addEventListener('change', e => this.onChange(e));
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.el.classList.contains('is-open')) this.close();
    });
  },

  async open() {
    if (!this.el) return;
    this.opener = document.activeElement;
    try { await this.refresh(); } catch (e) { /* show what Liquid rendered */ }
    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.el.querySelector('.pp-qc__close')?.focus({ preventScroll: true });
  },

  close() {
    if (!this.el) return;
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.opener?.focus?.({ preventScroll: true });
  },

  async refresh() {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=${this.sectionId}`);
    const data = await res.json();
    this.swap(data[this.sectionId]);
  },

  swap(html) {
    if (!html) return;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-drawer-content]');
    const current = this.el.querySelector('[data-drawer-content]');
    if (!next || !current) return;

    // Carry over what the customer had open and where focus was
    const focusId = document.activeElement?.closest?.('[data-focus-id]')?.dataset.focusId;
    current.querySelectorAll('details[open][id]').forEach(d => {
      const twin = next.querySelector(`#${d.id}`);
      if (twin) twin.open = true;
    });
    const scroll = current.querySelector('.pp-qc__scroll')?.scrollTop || 0;

    current.replaceWith(next);

    const scroller = next.querySelector('.pp-qc__scroll');
    if (scroller) scroller.scrollTop = scroll;
    this.setBreakdown(this.breakdownOpen);
    if (focusId) next.querySelector(`[data-focus-id="${focusId}"]`)?.focus({ preventScroll: true });

    updateCartCounts(parseInt(next.dataset.itemCount, 10) || 0);
  },

  async post(endpoint, body) {
    if (this.busy) return;
    this.busy = true;
    this.el.classList.add('is-busy');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/${endpoint}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionId, sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
      this.swap(data.sections?.[this.sectionId]);
    } catch (err) {
      this.showError(err.message);
    } finally {
      this.busy = false;
      this.el.classList.remove('is-busy');
    }
  },

  showError(msg) {
    const el = this.el.querySelector('[data-drawer-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  },

  setBreakdown(open) {
    this.breakdownOpen = open;
    const panel = this.el.querySelector('[data-breakdown]');
    const toggle = this.el.querySelector('[data-breakdown-toggle]');
    if (panel) panel.hidden = !open;
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
  },

  onClick(e) {
    if (e.target.closest('[data-drawer-close]')) return this.close();

    const line = e.target.closest('[data-line-change]');
    if (line) return this.post('change', { id: line.dataset.key, quantity: Math.max(0, parseInt(line.dataset.lineChange, 10)) });

    const apply = e.target.closest('[data-coupon-apply]');
    if (apply) return this.post('update', { discount: apply.dataset.couponApply });

    if (e.target.closest('[data-coupon-remove]')) return this.post('update', { discount: '' });

    if (e.target.closest('[data-breakdown-toggle]')) return this.setBreakdown(!this.breakdownOpen);
  },

  onSubmit(e) {
    const form = e.target.closest('[data-coupon-form]');
    if (!form) return; // the checkout form must submit natively for quick checkout apps
    e.preventDefault();
    const code = form.elements.discount.value.trim();
    if (code) this.post('update', { discount: code });
  },

  // Save the note as soon as it is edited, so it survives a quick checkout app
  // that intercepts Pay Now before the form posts
  onChange(e) {
    const note = e.target.closest('[data-drawer-note]');
    if (!note) return;
    fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: note.value })
    }).catch(() => {});
  }
};

/* ─── SEARCH OVERLAY ─── */
const SearchOverlay = {
  el: null,
  init() {
    this.el = document.getElementById('search-overlay');
    if (!this.el) return;
    this.el.setAttribute('aria-hidden', 'true');
    document.getElementById('search-close')?.addEventListener('click', () => this.close());
    this.el.addEventListener('click', e => { if (e.target === this.el) this.close(); });
    document.querySelectorAll('[data-open-search]').forEach(btn => btn.addEventListener('click', () => this.open()));
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
  },

  open() {
    if (!this.el) return;
    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => document.getElementById('search-input')?.focus(), 100);
  },

  close() {
    if (!this.el) return;
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
};

/* ─── MOBILE MENU ─── */
const MobileMenu = {
  el: null,
  init() {
    this.el = document.getElementById('mobile-menu');
    if (!this.el) return;
    this.el.setAttribute('aria-hidden', 'true');
    const hamburgerBtn = document.getElementById('hamburger-btn');
    hamburgerBtn?.addEventListener('click', () => this.open());
    document.getElementById('mobile-menu-close')?.addEventListener('click', () => this.close());
    this.el.querySelector('.pp-mobile-menu__overlay')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

    this.el.querySelectorAll('.pp-mobile-menu__accordion-trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const accordion = trigger.closest('.pp-mobile-menu__accordion');
        accordion?.classList.toggle('is-open');
      });
    });
  },

  open() {
    if (!this.el) return;
    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');
    const hamburgerBtn = document.getElementById('hamburger-btn');
    if (hamburgerBtn) hamburgerBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  },

  close() {
    if (!this.el) return;
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    const hamburgerBtn = document.getElementById('hamburger-btn');
    if (hamburgerBtn) hamburgerBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }
};

/* ─── SCROLL HEADER ─── */
const ScrollHeader = {
  header: null,
  init() {
    this.header = document.querySelector('[data-header]') || document.querySelector('.pp-header');
    if (!this.header) return;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.header.classList.toggle('is-stuck', window.scrollY > 40);
          this.header.classList.toggle('scrolled', window.scrollY > 80);
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }
};

/* ─── WISHLIST ─── */
const Wishlist = {
  key: 'pp_wishlist',
  get() {
    try { return JSON.parse(localStorage.getItem(this.key) || '[]'); } catch { return []; }
  },
  save(list) {
    localStorage.setItem(this.key, JSON.stringify(list));
  },
  toggle(productId) {
    const list = this.get();
    const idx = list.indexOf(productId);
    if (idx === -1) { list.push(productId); } else { list.splice(idx, 1); }
    this.save(list);
    return idx === -1;
  },
  has(productId) { return this.get().includes(productId); },

  init() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const id = btn.dataset.wishlistBtn;
      if (this.has(id)) btn.classList.add('is-wishlisted');
      btn.addEventListener('click', () => {
        const added = this.toggle(id);
        btn.classList.toggle('is-wishlisted', added);
        this.showNotif(added ? 'Added to wishlist 🤍' : 'Removed from wishlist');
      });
    });
  },

  showNotif(msg) {
    let notif = document.getElementById('pp-notif');
    if (!notif) {
      notif = document.createElement('div');
      notif.id = 'pp-notif';
      notif.style.cssText = 'position:fixed;bottom:32px;left:50%;transform:translateX(-50%);background:var(--pp-charcoal);color:var(--pp-white);padding:12px 24px;border-radius:50px;font-family:var(--font-body);font-size:14px;z-index:9999;transition:opacity 0.3s ease;pointer-events:none;';
      document.body.appendChild(notif);
    }
    notif.textContent = msg;
    notif.style.opacity = '1';
    clearTimeout(notif._timer);
    notif._timer = setTimeout(() => { notif.style.opacity = '0'; }, 2500);
  }
};

/* ─── ADD TO CART BUTTONS ─── */
function initAddToCart() {
  document.querySelectorAll('[data-add-to-cart]').forEach(btn => {
    btn.addEventListener('click', async e => {
      e.preventDefault();
      const variantId = btn.dataset.variantId || btn.dataset.addToCart;
      if (!variantId) return;
      const originalText = btn.textContent;
      btn.textContent = 'Adding...';
      btn.disabled = true;
      try {
        await PastelCart.add(variantId);
        btn.textContent = 'Added! 🛍️';
        setTimeout(() => CartDrawer.open(), 300);
        setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2000);
      } catch (err) {
        btn.textContent = 'Out of stock';
        setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2000);
      }
    });
  });
}

/* ─── BUY NOW BUTTONS ─── */
function initBuyNow() {
  document.querySelectorAll('[data-buy-now]').forEach(btn => {
    btn.addEventListener('click', async e => {
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      const originalText = btn.textContent;
      btn.textContent = 'Please wait...';
      btn.disabled = true;
      try {
        await PastelCart.add(variantId);
        window.location.href = '/checkout';
      } catch (err) {
        btn.textContent = 'Out of stock';
        setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2000);
      }
    });
  });
}

/* ─── ACCORDION ─── */
function initAccordions() {
  document.querySelectorAll('.pp-accordion__trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const accordion = trigger.closest('.pp-accordion');
      const isOpen = accordion.classList.contains('is-open');
      document.querySelectorAll('.pp-accordion.is-open').forEach(a => a.classList.remove('is-open'));
      if (!isOpen) accordion.classList.add('is-open');
    });
  });
}

/* ─── TABS (FEATURED PRODUCTS) ─── */
function initTabs() {
  document.querySelectorAll('.pp-products__tabs').forEach(tabsContainer => {
    const section = tabsContainer.closest('.pp-products');
    const grid = section?.querySelector('.pp-products__grid');
    if (!grid) return;

    // "All" shows only cards explicitly curated for it (data-category="all"), so the tab
    // stays limited to the merchant's chosen picks instead of every category combined.
    // Falls back to showing everything if no curated products were configured.
    const hasCuratedAll = !!grid.querySelector('[data-category="all"]');

    tabsContainer.querySelectorAll('.pp-tab-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const category = btn.dataset.category;
        tabsContainer.querySelectorAll('.pp-tab-btn').forEach(b => {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');

        grid.querySelectorAll('[data-category]').forEach(card => {
          const matches = category === 'all'
            ? (hasCuratedAll ? card.dataset.category === 'all' : true)
            : card.dataset.category === category;
          if (matches) {
            card.classList.add('is-visible');
            card.style.display = '';
          } else {
            card.classList.remove('is-visible');
            card.style.display = 'none';
          }
        });
      });
    });

    tabsContainer.querySelector('.pp-tab-btn[data-category="all"]')?.click();
  });
}

/* ─── COLLECTION GRID TOGGLE ─── */
function initGridToggle() {
  document.querySelectorAll('.pp-grid-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.pp-grid-btn').forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      const cols = btn.dataset.cols;
      const grid = document.querySelector('.pp-collection-grid');
      if (grid) {
        grid.className = grid.className.replace(/cols-\d/g, '').trim();
        if (cols !== '4') grid.classList.add(`cols-${cols}`);
      }
    });
  });
}

/* ─── PRODUCT PAGE — SIZE SELECTOR ─── */
function initSizeSelector() {
  document.querySelectorAll('.pp-size-pill').forEach(pill => {
    pill.addEventListener('click', () => {
      document.querySelectorAll('.pp-size-pill').forEach(p => p.classList.remove('is-active'));
      pill.classList.add('is-active');
    });
  });
}

/* ─── PRODUCT PAGE — GALLERY ─── */
function initGallery() {
  const mainImg = document.querySelector('.pp-gallery__main-img');
  if (!mainImg) return;
  document.querySelectorAll('.pp-gallery__thumb').forEach(thumb => {
    thumb.addEventListener('click', () => {
      document.querySelectorAll('.pp-gallery__thumb').forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
      const src = thumb.querySelector('img')?.src;
      if (src && mainImg) {
        mainImg.style.opacity = '0';
        setTimeout(() => { mainImg.src = src; mainImg.style.opacity = '1'; }, 200);
        mainImg.style.transition = 'opacity 0.3s ease';
      }
    });
  });
}

/* ─── QUANTITY SELECTOR (PRODUCT PAGE) ─── */
function initQtySelector() {
  const input = document.querySelector('.pp-product-qty-input');
  document.querySelectorAll('.pp-product-qty-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      if (!input) return;
      let val = parseInt(input.value) || 1;
      if (btn.dataset.action === 'plus') val = Math.min(val + 1, 99);
      if (btn.dataset.action === 'minus') val = Math.max(val - 1, 1);
      input.value = val;
    });
  });
}

/* ─── SIZE GUIDE MODAL ─── */
function initSizeModal() {
  const modal = document.getElementById('size-guide-modal');
  document.querySelectorAll('[data-open-size-guide]').forEach(btn => {
    btn.addEventListener('click', () => {
      if (modal) { modal.classList.add('is-open'); modal.setAttribute('aria-hidden', 'false'); document.body.style.overflow = 'hidden'; }
    });
  });
  document.querySelectorAll('[data-close-size-guide]').forEach(btn => {
    btn.addEventListener('click', () => {
      if (modal) { modal.classList.remove('is-open'); modal.setAttribute('aria-hidden', 'true'); document.body.style.overflow = ''; }
    });
  });
  modal?.querySelector('.pp-size-modal__overlay')?.addEventListener('click', () => {
    modal.classList.remove('is-open'); modal.setAttribute('aria-hidden', 'true'); document.body.style.overflow = '';
  });
}

/* ─── CART PAGE ─── */
async function initCartPage() {
  if (!document.querySelector('.pp-cart-page')) return;
  document.querySelectorAll('.pp-cart-qty-minus, .pp-cart-qty-plus').forEach(btn => {
    btn.addEventListener('click', async () => {
      const row = btn.closest('[data-key]');
      const key = row?.dataset.key;
      const valEl = row?.querySelector('.pp-qty-val');
      if (!key || !valEl) return;
      let qty = parseInt(valEl.textContent);
      if (btn.classList.contains('pp-cart-qty-plus')) qty++;
      else qty = Math.max(0, qty - 1);
      await PastelCart.change(key, qty);
      location.reload();
    });
  });
}

/* ─── NOTIFY BAR DISMISS ─── */
function initCartIconTrigger() {
  document.querySelectorAll('[data-open-cart]').forEach(btn => {
    btn.addEventListener('click', e => { e.preventDefault(); CartDrawer.open(); });
  });
}

function removeDefaultProductOption() {
  document.querySelectorAll('.pp-product-page .pp-option-label').forEach(label => {
    const text = label.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
    if (!text.includes('title:') || !text.includes('default title')) return;
    const optionGroup = label.nextElementSibling;
    label.remove();
    if (optionGroup?.classList.contains('pp-size-pills')) optionGroup.remove();
  });
}

/* ─── COLLECTION MARQUEE ─── */
function initMarquee() {
  document.querySelectorAll('.pp-marquee__row').forEach(row => {
    const track = row.querySelector('.pp-marquee__track');
    if (!track) return;
    const set = track.querySelector('.pp-marquee__set');
    if (!set) return;

    const isReverse = row.classList.contains('pp-marquee__row--rev');
    const speed = 0.6;
    const gap = 16;
    let pos = isReverse ? -set.offsetWidth - gap : 0;
    let paused = false;

    row.addEventListener('mouseenter', () => { paused = true; });
    row.addEventListener('mouseleave', () => { paused = false; });
    row.addEventListener('touchstart', () => { paused = true; }, { passive: true });
    row.addEventListener('touchend', () => { setTimeout(() => { paused = false; }, 2000); }, { passive: true });

    function tick() {
      if (!paused) {
        if (isReverse) {
          pos += speed;
          if (pos >= 0) pos = -set.offsetWidth - gap;
        } else {
          pos -= speed;
          if (pos <= -(set.offsetWidth + gap)) pos = 0;
        }
        track.style.transform = 'translateX(' + pos + 'px)';
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  });
}

/* ─── CART COUNT BADGES ─── */
function updateCartCounts(count) {
  document.querySelectorAll('[data-cart-count]').forEach(el => {
    el.textContent = count;
    el.hidden = count === 0;
  });
  // Pre-2.0 badge markup, still present in some sections
  document.querySelectorAll('.pp-cart-badge').forEach(el => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
}

/* ─── ANNOUNCEMENT BAR ─── */
function initAnnounceBar() {
  document.querySelectorAll('[data-announce]').forEach(bar => {
    const items = Array.from(bar.querySelectorAll('[data-announce-item]'));
    if (items.length < 2 || bar.dataset.rotate !== 'true') return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    const interval = parseInt(bar.dataset.interval, 10) || 5000;
    let index = 0;
    let timer = null;

    function show(next) {
      items[index].classList.remove('is-active');
      items[index].setAttribute('aria-hidden', 'true');
      index = next;
      items[index].classList.add('is-active');
      items[index].removeAttribute('aria-hidden');
    }

    function start() {
      stop();
      timer = window.setInterval(() => show((index + 1) % items.length), interval);
    }

    function stop() {
      if (timer) window.clearInterval(timer);
      timer = null;
    }

    bar.addEventListener('mouseenter', stop);
    bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', stop);
    bar.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

    start();
  });
}

/* ─── VIDEOS THAT PLAY WHEN SEEN ───
   Shared by any section that drops a silent background clip in. Nothing is
   fetched until the clip is near the viewport, and it pauses on the way out. */
function initInViewVideos() {
  var wraps = document.querySelectorAll('[data-inview-video]');
  if (!wraps.length) return;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  wraps.forEach(function (wrap) {
    var video = wrap.querySelector('video');
    if (!video) return;

    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');

    if (reduced || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          if (video.preload === 'none') video.preload = 'auto';
          var playing = video.play();
          if (playing && typeof playing.catch === 'function') playing.catch(function () {});
        } else {
          video.pause();
        }
      });
    }, { rootMargin: '200px', threshold: 0.25 });

    observer.observe(video);
  });
}

/* ─── RAIL ARROWS ───
   Scrolling rails are obvious on touch but easy to miss with a mouse, so the
   arrows only appear where there is something to scroll to. */
function initRails() {
  document.querySelectorAll('[data-rail-group]').forEach(function (group) {
    var rail = group.querySelector('[data-rail]');
    var prev = group.querySelector('[data-rail-prev]');
    var next = group.querySelector('[data-rail-next]');
    if (!rail || !prev || !next) return;

    function step() {
      var first = rail.firstElementChild;
      if (!first) return rail.clientWidth * 0.8;
      var gap = parseFloat(getComputedStyle(rail).columnGap) || 16;
      return first.getBoundingClientRect().width + gap;
    }

    function sync() {
      var max = rail.scrollWidth - rail.clientWidth;
      // A couple of pixels of slack: sub-pixel widths never land exactly on the end
      var scrollable = max > 4;
      prev.hidden = !scrollable || rail.scrollLeft <= 2;
      next.hidden = !scrollable || rail.scrollLeft >= max - 2;
    }

    prev.addEventListener('click', function () {
      rail.scrollBy({ left: -step(), behavior: 'smooth' });
    });

    next.addEventListener('click', function () {
      rail.scrollBy({ left: step(), behavior: 'smooth' });
    });

    var ticking = false;
    rail.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { sync(); ticking = false; });
    }, { passive: true });

    window.addEventListener('resize', sync);

    if ('ResizeObserver' in window) {
      new ResizeObserver(sync).observe(rail);
    }

    sync();
  });
}

/* ─── INSTAGRAM REELS ───
   Videos ship with preload="none" so nothing downloads on page load. Each clip
   starts only once it scrolls into view, pauses when it leaves, and loops back
   to the start after the section's cap so a long reel never runs on. */
function initReels() {
  document.querySelectorAll('[data-reels]').forEach(function (section) {
    var max = parseFloat(section.dataset.maxSeconds) || 15;
    var videos = Array.prototype.slice.call(section.querySelectorAll('video'));
    if (!videos.length) return;

    videos.forEach(function (video) {
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');

      video.addEventListener('timeupdate', function () {
        if (video.currentTime >= max) video.currentTime = 0;
      });
    });

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting) {
          if (video.preload === 'none') video.preload = 'auto';
          var playing = video.play();
          // Autoplay can still be refused; the poster stays visible, which is fine
          if (playing && typeof playing.catch === 'function') playing.catch(function () {});
        } else {
          video.pause();
        }
      });
    }, { rootMargin: '150px', threshold: 0.35 });

    videos.forEach(function (video) { observer.observe(video); });
  });
}

/* ─── FOOTER ACCORDIONS ───
   Footer columns collapse on mobile and stay open on desktop. CSS alone cannot
   do this reliably: current Chrome hides closed <details> content through
   ::details-content, which a display rule on the child element cannot reach.
   Setting the open property is the one approach that works in every browser. */
function initFooterAccordions() {
  var panels = document.querySelectorAll('.pp-ftr__acc');
  if (!panels.length) return;

  var desktop = window.matchMedia('(min-width: 990px)');

  function sync() {
    panels.forEach(function (panel) {
      panel.open = desktop.matches;
    });
  }

  sync();

  if (typeof desktop.addEventListener === 'function') {
    desktop.addEventListener('change', sync);
  } else if (typeof desktop.addListener === 'function') {
    desktop.addListener(sync);
  }
}

/* ─── HERO SLIDER ─── */
function initHeroSlider() {
  document.querySelectorAll('[data-hero]').forEach(hero => {
    const track = hero.querySelector('[data-hero-track]');
    const slides = Array.from(hero.querySelectorAll('[data-hero-slide]'));
    const dots = Array.from(hero.querySelectorAll('[data-hero-dot]'));
    if (!track || slides.length < 2) return;

    const count = slides.length;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Slides after the first arrive with data-src/data-srcset (sections/hero-banner.liquid)
    // so they don't compete with the first image for bandwidth. Swap them in once
    // the page has loaded, or as soon as the customer moves the slider.
    function loadDeferred() {
      hero.querySelectorAll('[data-srcset], [data-src]').forEach(el => {
        if (el.dataset.srcset) { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); }
        if (el.dataset.src) { el.src = el.dataset.src; el.removeAttribute('data-src'); }
      });
    }
    if (document.readyState === 'complete') loadDeferred();
    else window.addEventListener('load', loadDeferred, { once: true });
    const interval = parseInt(hero.dataset.interval, 10) || 6000;
    let pos = 0;
    let timer = null;

    // A copy of the first slide sits after the last one. Advancing past the end
    // slides forward onto something identical to the start, so snapping back to
    // position zero happens behind an identical frame and is never seen. With
    // reduced motion there is no animation to hide it behind, and none needed.
    if (!reduced) {
      const clone = slides[0].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-hero-slide');
      clone.removeAttribute('id');
      track.appendChild(clone);
    }

    function paint() {
      track.style.transform = 'translateX(-' + pos * 100 + '%)';
      const current = pos % count;
      slides.forEach((slide, i) => {
        const on = i === current;
        slide.classList.toggle('is-active', on);
        if (on) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      dots.forEach((dot, i) => {
        const on = i === current;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    // Moves without animating, used to line things up before a real move.
    function jump(to) {
      track.style.transition = 'none';
      pos = to;
      paint();
      void track.offsetWidth;
      track.style.transition = '';
    }

    function next() {
      loadDeferred();
      if (reduced) pos = (pos + 1) % count;
      else pos = pos + 1 > count ? 1 : pos + 1;
      paint();
    }

    function prev() {
      loadDeferred();
      if (pos === 0) {
        if (reduced) {
          pos = count - 1;
          paint();
          return;
        }
        // Sit on the copy at the far end first, so going back from the first
        // slide slides backwards instead of rewinding across the whole strip.
        jump(count);
      }
      pos -= 1;
      paint();
    }

    track.addEventListener('transitionend', e => {
      if (e.propertyName === 'transform' && pos === count) jump(0);
    });

    function start() {
      if (reduced || hero.dataset.autoplay !== 'true') return;
      stop();
      timer = window.setInterval(next, interval);
    }

    function stop() {
      if (timer) window.clearInterval(timer);
      timer = null;
    }

    dots.forEach(dot => {
      dot.addEventListener('click', () => {
        loadDeferred();
        pos = parseInt(dot.dataset.heroDot, 10);
        paint();
        start();
      });
    });

    // Swipe
    let startX = null;
    hero.addEventListener('touchstart', e => { loadDeferred(); startX = e.touches[0].clientX; stop(); }, { passive: true });
    hero.addEventListener('touchend', e => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 45) (dx < 0 ? next : prev)();
      startX = null;
      start();
    }, { passive: true });

    // Hovering the banner itself must not pause it: on a desktop it spans the
    // whole viewport width, so the pointer rests over it most of the time and
    // the slideshow would simply sit still. Only the dots pause it, so aiming
    // at one does not get the slide swapped mid-click.
    const dotsBar = hero.querySelector('.pp-hero2__dots');
    if (dotsBar) {
      dotsBar.addEventListener('mouseenter', stop);
      dotsBar.addEventListener('mouseleave', start);
    }

    hero.addEventListener('focusin', stop);
    hero.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

    paint();
    start();
  });
}

/* ─── QUICK ADD (touch: no hover, so size chips need a sheet) ─── */
function initQuickAdd() {
  document.querySelectorAll('[data-quick-add]').forEach(btn => {
    btn.addEventListener('click', () => {
      const card = btn.closest('.pp-card2');
      const sizes = card && card.querySelector('[data-card-sizes]');
      if (!sizes) {
        window.location.href = btn.dataset.productUrl;
        return;
      }
      // On hover-capable screens the chips are already visible; just reveal them.
      if (window.matchMedia('(hover: hover)').matches) {
        card.classList.add('is-sizes-open');
        return;
      }
      openQuickAddSheet(card, sizes);
    });
  });
}

function openQuickAddSheet(card, sizes) {
  const existing = document.getElementById('pp-quickadd');
  if (existing) existing.remove();

  const title = card.querySelector('.pp-card2__title');
  const sheet = document.createElement('div');
  sheet.id = 'pp-quickadd';
  sheet.className = 'pp-quickadd is-open';
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-label', 'Choose a size');

  const panel = document.createElement('div');
  panel.className = 'pp-quickadd__panel';

  const heading = document.createElement('p');
  heading.className = 'pp-quickadd__title';
  heading.textContent = title ? title.textContent.trim() : 'Choose a size';

  const label = document.createElement('p');
  label.className = 'pp-quickadd__label';
  label.textContent = 'Select a size';

  const list = document.createElement('div');
  list.className = 'pp-quickadd__sizes';
  sizes.querySelectorAll('button').forEach(chip => {
    const clone = chip.cloneNode(true);
    clone.classList.add('pp-quickadd__size');
    list.appendChild(clone);
  });

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'pp-quickadd__close';
  close.setAttribute('aria-label', 'Close');
  close.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15 5L5 15M5 5l10 10"/></svg>';

  const overlay = document.createElement('div');
  overlay.className = 'pp-quickadd__overlay';

  panel.append(close, heading, label, list);
  sheet.append(overlay, panel);
  document.body.appendChild(sheet);
  document.body.style.overflow = 'hidden';

  function dismiss() {
    sheet.remove();
    document.body.style.overflow = '';
  }

  overlay.addEventListener('click', dismiss);
  close.addEventListener('click', dismiss);
  document.addEventListener('keydown', function esc(e) {
    if (e.key === 'Escape') { dismiss(); document.removeEventListener('keydown', esc); }
  });
  list.addEventListener('click', e => {
    if (e.target.closest('button')) window.setTimeout(dismiss, 350);
  });

  const firstChip = list.querySelector('button:not([disabled])');
  if (firstChip) firstChip.focus();
}

/* ─── LAZY VIDEO POSTERS ─── */
/* Posters arrive as data-poster (sections/instagram-reels.liquid, brand-story.liquid):
   a poster attribute is fetched as soon as the page is read, however far down. */
function initLazyPosters() {
  const videos = document.querySelectorAll('video[data-poster]');
  if (!videos.length) return;
  const show = v => { v.poster = v.dataset.poster; v.removeAttribute('data-poster'); };
  if (!('IntersectionObserver' in window)) { videos.forEach(show); return; }
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => { if (e.isIntersecting) { show(e.target); io.unobserve(e.target); } });
  }, { rootMargin: '600px' });
  videos.forEach(v => io.observe(v));
}

/* ─── INIT ALL ─── */
document.addEventListener('DOMContentLoaded', () => {
  CartDrawer.init();
  SearchOverlay.init();
  MobileMenu.init();
  ScrollHeader.init();
  Wishlist.init();
  initAddToCart();
  initBuyNow();
  initAccordions();
  initTabs();
  initGridToggle();
  initSizeSelector();
  initGallery();
  initQtySelector();
  initSizeModal();
  initCartPage();
  removeDefaultProductOption();
  initCartIconTrigger();
  initMarquee();
  initAnnounceBar();
  initQuickAdd();
  initHeroSlider();
  initFooterAccordions();
  initReels();
  initRails();
  initInViewVideos();
  initLazyPosters();

  PastelCart.get().then(cart => updateCartCounts(cart.item_count));
});
